// =============================================================================
// AllocationGrid.jsx - Time Slot Grid Component 
// =============================================================================
// Displays the visual grid of time slots with pitch sections and allocations
// - Renders time slots with expand/collapse functionality
// - Shows pitch visualization using PitchRenderer
// - Displays reference points around pitch
// - Handles grass area rendering if enabled
// - Shows allocation details with color coding
// - Mobile responsive with collapsible slots
// =============================================================================

import React from 'react';
import PitchRenderer from '../PitchRenderer';

/**
 * AllocationGrid Component
 * 
 * @param {Object} props
 * @param {string} props.date - Current date (YYYY-MM-DD)
 * @param {string} props.pitchId - Pitch ID
 * @param {string} props.normalizedPitchId - Normalized pitch ID for lookups
 * @param {Array<string>} props.timeSlots - Array of time slots to display
 * @param {Object} props.allocations - All allocations keyed by allocation key
 * @param {Object} props.expandedSlots - Object tracking which slots are expanded
 * @param {Function} props.onToggleSlot - Handler for expanding/collapsing slots
 * @param {Object} props.referencePoints - Pitch reference points (top/bottom/left/right)
 * @param {Object} props.showGrassArea - Whether to show grass area for pitch
 * @param {Object} props.pitchSectionConfig - Section configuration for pitch
 * @param {Set} props.deletingKeys - Set of allocation keys currently being deleted
 * @param {boolean} props.canEdit - Whether user can edit allocations
 * @param {Function} props.onClearAllocation - Handler for clearing an allocation
 * @param {Function} props.onUpdateAllocation - Handler for updating an allocation (e.g., opponent)
 * @param {Function} props.isLightColor - Helper function to determine if color is light
 * @param {Array} props.teams - Array of team objects for team name lookup
 */
const AllocationGrid = ({
  date,
  pitchId,
  normalizedPitchId,
  timeSlots,
  slotWindowMap,
  allocations,
  expandedSlots,
  onToggleSlot,
  referencePoints,
  showGrassArea,
  pitchSectionConfig,
  deletingKeys,
  canEdit,
  onClearAllocation,
  onUpdateAllocation, // NEW: Handler for updating allocation fields like opponent
  isLightColor,
  teams = []
}) => {
  
  // Helper function to get team name from team ID
  const getTeamName = (allocation) => {
    if (!allocation) return 'Unknown Team';
    
    // If allocation already has a team name field that doesn't look like an ID, use it
    if (allocation.team && !allocation.team.startsWith('team-')) {
      return allocation.team;
    }
    
    // Otherwise, look up the team by ID
    const teamId = allocation.teamId || allocation.team;
    if (!teamId) {
      return 'Unknown Team';
    }
    
    const teamObj = teams.find(t => t.id === teamId);
    return teamObj?.name || teamId;
  };
  
  // Get reference points for current pitch
  const getCurrentPitchReferences = () => {
    // Extract the pitch number from various formats
    const pitchNumber = pitchId?.toString().replace(/^pitch-?/i, '') || '';
    
    // Try multiple key formats - reference points are stored with normalized keys (dash removed)
    const possibleReferenceKeys = [
      `pitch${pitchNumber}`,           // e.g., "pitch1" - this is how they're stored after normalization
      normalizedPitchId?.replace('-', ''), // e.g., "pitch1" from "pitch-1"
      `pitch-${pitchNumber}`,          // e.g., "pitch-1"
      normalizedPitchId,               // e.g., "pitch-1"
      pitchId,                         // raw pitchId
      pitchNumber,                     // just the number
    ].filter(Boolean);
    
    console.log('[AllocationGrid] Looking for reference points:');
    console.log('  pitchId:', pitchId);
    console.log('  pitchNumber:', pitchNumber);
    console.log('  normalizedPitchId:', normalizedPitchId);
    console.log('  possibleReferenceKeys:', possibleReferenceKeys);
    console.log('  available referencePoints keys:', Object.keys(referencePoints));
    
    let currentPitchReferences = {};
    for (const key of possibleReferenceKeys) {
      if (key && referencePoints[key]) {
        console.log('  FOUND match with key:', key, '-> ', referencePoints[key]);
        currentPitchReferences = referencePoints[key];
        break;
      }
    }
    
    if (Object.keys(currentPitchReferences).length === 0) {
      console.log('  NO reference points found for this pitch');
    }
    
    return currentPitchReferences;
  };
  
  // Check if grass area is enabled for current pitch
  const isGrassAreaEnabled = () => {
    const possibleKeys = [
      normalizedPitchId,
      `pitch-${pitchId}`,
      pitchId,
      `Pitch ${pitchId}`,
      `Pitch-${pitchId}`
    ];
    
    return possibleKeys.some(key => showGrassArea[key] === true);
  };
  
  // Render pitch section with reference points
  const renderPitchSection = (timeSlot) => {
    const currentPitchReferences = getCurrentPitchReferences();
    const grassAreaEnabled = isGrassAreaEnabled();
    const windowSlots = slotWindowMap ? (slotWindowMap[timeSlot] || [timeSlot]) : [timeSlot];
    
    return (
      <div className="pitch-container" style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        maxWidth: '400px'
      }}>
        {/* Pitch renderer with reference points */}
        <PitchRenderer 
          pitchSectionConfig={pitchSectionConfig}
          timeSlot={timeSlot}
          windowSlots={windowSlots}
          filteredAllocations={allocations}
          normalizedPitchId={normalizedPitchId}
          deletingKeys={deletingKeys}
          canEdit={canEdit}
          clearAllocation={onClearAllocation}
          onUpdateAllocation={onUpdateAllocation}
          date={date}
          teams={teams}
          referencePoints={currentPitchReferences}
        />

        {/* Grass area (if enabled) */}
        {grassAreaEnabled && (
          <div style={{
            width: '280px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4px',
            height: '104px',
            margin: '0 auto'
          }}>
            <div style={{
              position: 'relative',
              backgroundColor: '#dcfce7',
              border: '4px solid white',
              borderRadius: '8px',
              padding: '8px'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#bbf7d0',
                borderRadius: '8px'
              }}></div>
              
              <div style={{ position: 'relative', zIndex: 10, height: '100%' }}>
                {(() => {
                  const key = `${date}-${timeSlot}-${normalizedPitchId}-grass`;
                  const allocation = allocations[key];
                  const isDeleting = deletingKeys.has(key);
                  const teamName = getTeamName(allocation);
                  
                  return (
                    <div 
                      style={{
                        height: '100%',
                        border: '2px solid white',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        cursor: allocation && canEdit && !isDeleting ? 'pointer' : 'default',
                        backgroundColor: allocation ? (allocation.colour || allocation.color) + '90' : 'rgba(255,255,255,0.1)',
                        borderColor: allocation ? (allocation.colour || allocation.color) : 'rgba(255,255,255,0.5)',
                        color: allocation ? (isLightColor(allocation.colour || allocation.color) ? '#000' : '#fff') : '#374151',
                        opacity: isDeleting ? 0.5 : 1,
                        pointerEvents: isDeleting ? 'none' : 'auto',
                        position: 'relative'
                      }}
                      onClick={() => allocation && canEdit && !isDeleting && onClearAllocation(key)}
                      title={allocation 
                        ? canEdit 
                          ? `Click to remove ${teamName}'s allocation` 
                          : `${teamName} - ${allocation.type}` 
                        : canEdit 
                          ? 'Click to allocate grass area' 
                          : 'Available'
                      }
                    >
                      {allocation && (
                        <span style={{ 
                          fontSize: '12px', 
                          textAlign: 'center',
                          lineHeight: '1.2',
                          padding: '2px'
                        }}>
                          {teamName}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            
            {/* Second grass section */}
            <div style={{
              position: 'relative',
              backgroundColor: '#dcfce7',
              border: '4px solid white',
              borderRadius: '8px',
              padding: '8px'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#bbf7d0',
                borderRadius: '8px'
              }}></div>
              
              <div style={{ position: 'relative', zIndex: 10, height: '100%' }}>
                {(() => {
                  const key = `${date}-${timeSlot}-${normalizedPitchId}-grass2`;
                  const allocation = allocations[key];
                  const isDeleting = deletingKeys.has(key);
                  const teamName = getTeamName(allocation);
                  
                  return (
                    <div 
                      style={{
                        height: '100%',
                        border: '2px solid white',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        cursor: allocation && canEdit && !isDeleting ? 'pointer' : 'default',
                        backgroundColor: allocation ? (allocation.colour || allocation.color) + '90' : 'rgba(255,255,255,0.1)',
                        borderColor: allocation ? (allocation.colour || allocation.color) : 'rgba(255,255,255,0.5)',
                        color: allocation ? (isLightColor(allocation.colour || allocation.color) ? '#000' : '#fff') : '#374151',
                        opacity: isDeleting ? 0.5 : 1,
                        pointerEvents: isDeleting ? 'none' : 'auto',
                        position: 'relative'
                      }}
                      onClick={() => allocation && canEdit && !isDeleting && onClearAllocation(key)}
                      title={allocation 
                        ? canEdit 
                          ? `Click to remove ${teamName}'s allocation` 
                          : `${teamName} - ${allocation.type}` 
                        : canEdit 
                          ? 'Click to allocate grass area' 
                          : 'Available'
                      }
                    >
                      {allocation && (
                        <span style={{ 
                          fontSize: '12px', 
                          textAlign: 'center',
                          lineHeight: '1.2',
                          padding: '2px'
                        }}>
                          {teamName}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'center',
      width: '100%'
    }}>
      {timeSlots.map(timeSlot => {
        // Get allocations for this time slot (or all sub-slots if grouped)
        const windowSlots = slotWindowMap ? (slotWindowMap[timeSlot] || [timeSlot]) : [timeSlot];
        const slotAllocations = Object.entries(allocations).filter(([key]) =>
          windowSlots.some(ws => key.includes(`-${ws}-`))
        );
        const hasAllocations = slotAllocations.length > 0;
        
        // Get unique teams for this slot - use getTeamName for display
        const uniqueTeams = [...new Set(slotAllocations.map(([, a]) => getTeamName(a)))];
        
        return (
          <div key={timeSlot} style={{
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '600px'
          }}>
            {/* Time slot header - clickable to expand/collapse */}
            <div
              onClick={() => onToggleSlot(timeSlot)}
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                minHeight: '56px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: 1
              }}>
                {/* Time badge */}
                <span style={{
                  backgroundColor: hasAllocations ? '#dbeafe' : '#f3f4f6',
                  color: hasAllocations ? '#1e40af' : '#6b7280',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  border: '1px solid',
                  borderColor: hasAllocations ? '#93c5fd' : '#e5e7eb',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '80px'
                }}>
                  {(() => {
                    if (!slotWindowMap) return timeSlot;
                    const window = slotWindowMap[timeSlot] || [];
                    if (window.length <= 1) return timeSlot;
                    // Calculate end time from last sub-slot + 15 min
                    const last = window[window.length - 1];
                    const [lh, lm] = last.split(':').map(Number);
                    const endTotal = lh * 60 + lm + 15;
                    const endH = Math.floor(endTotal / 60);
                    const endM = endTotal % 60;
                    const endStr = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;
                    return `${timeSlot} - ${endStr}`;
                  })()}
                </span>
                
                {/* Teams or availability indicator */}
                {hasAllocations ? (
                  <span style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    flex: 1
                  }}>
                    {uniqueTeams.join(', ')}
                  </span>
                ) : (
                  <span style={{
                    fontSize: '16px',
                    color: '#9ca3af',
                    flex: 1
                  }}>
                    Available
                  </span>
                )}
              </div>
              
              {/* Expand/collapse arrow */}
              <div style={{
                transform: expandedSlots[timeSlot] ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                fontSize: '20px',
                color: '#6b7280'
              }}>
                ▼
              </div>
            </div>
            
            {/* Expanded content - pitch visualization */}
            {expandedSlots[timeSlot] && (
              <div style={{
                padding: '16px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                {renderPitchSection(timeSlot)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AllocationGrid;
